import React, { FC, memo, useMemo, useContext, useCallback, useState, useEffect, useRef } from 'react';
import * as yup from 'yup';
import { format } from 'date-fns';
import omit from 'lodash/omit';

import GeneralDialogForm from '../../../../components/formHelpers/GeneralDialogForm';
import { ToggleDialog } from '../../../../components/Tables/TableWithModalEdit/modalFormPieces/IFormModal';
import { displayToast } from '../../../../components/RTToast';

import { getRef } from '../formHelpers';
// import { creditCardValidationSchema } from '../subForms/CreditCardSubForm';
import { Currency } from '../../../../api/currencies/types';
import { Voucher, voucherCertificationTypesEnum } from '../../../../api/vouchers/types';
import { Context as VouchersContext } from '../../../../api/vouchers/context';
import {
    calcVatOnTop,
    documentTypesEnum,
    documentTypesInitialsEnum,
    getVatPercent,
    paymentMethodTypeEnum,
    PaymentMethodTypes
} from '../../../../api/vouchers/helpers';
import { Context as CompaniesContext } from '../../../../api/companies/context';
import { getPdfCompanyInfo } from '../../../../api/companies/helpers';
import { Context as AuthContext } from '../../../../api/auth/context';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Context as ExchangeRatesContext } from '../../../../api/exchangeRates/context';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { Client } from '../../../../api/clients/types';
import { getToFixedNumber, taytanFormatNumberToString } from '../../../../utils/mathUtils';
import { currencyIdOf, repaidAgainstReceipt } from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/Repay/repaidAmounts';
import { resolveSourceDocumentRateForForm, sameCurrency } from '../../../../utils/currencyMath';
import {
    buildRemittanceAmountsTable,
    convertCoveredAmountToCovering
} from '../../../../api/vouchers/pdfBuilders/helpers';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../../components/Pdfs/PdfComponents/PdfTable';
import { PdfBoxDataType } from '../../../../components/Pdfs/Vouchers/voucherHelpers';
import ViewVoucherPdf from '../../../Reservations/TravelFile/TravelFileHomePage/ViewVoucherPdf';
import { voucherSourceTargetTypesEnum } from '../../../../api/travelFiles/constants/voucherSourceTarget';
import { BankAccount } from '../../../../api/bankAccounts/types';
import { CreditCard } from '../../../../api/creditCards/types';
import { PaymentsTerminal } from '../../../../api/paymentsTerminals/types';

import RemittanceForm from './RemittanceForm';
import { getDateByFormat } from '../../../../utils/dateUtils';
import useGetAgentInfoDetails from '../../useGetAgentInfoDetails';
import { getPaymentMethodObjectValidationSchema } from './bankTransferValidationSchema';

type Props = {
    open: boolean;
    toggleOpen: ToggleDialog;
    documentGenerationData: Partial<Voucher> | null;
    selectedDocuments: Array<Voucher> | null;
    onAddVoucher?: Function | any;
    // When set, the Remittance is issued straight from these values on mount
    // instead of via the form/card step. Used by the app-wide resume handler
    // after a Masleka card charge, where the form state was lost to the reload.
    autoIssueValues?: PreRemittanceState | null;
    /**
     * Every document on the travel file. On a repay from a receipt it gives what
     * was already paid back against that receipt, so the amount starts at what
     * is left rather than the full receipt again.
     */
    travelFileDocuments?: Array<Voucher> | null;
};
const DOCUMENTS_COLUMNS: Array<PdfTablePropsColumn> = [
    { title: 'Doc#', fieldName: 'number', style: { width: '12%' } },
    { title: 'Value Date', fieldName: 'dueDate', style: { width: '10.5%' } },
    { title: 'Travel File #', fieldName: 'travelFileNumber', style: { width: '12%' } },
    { title: 'Service Name', fieldName: 'serviceName', style: { width: '13%' } },
    { title: 'Ref.', fieldName: 'ref', style: { width: '9.5%' } },
    { title: 'Amount', fieldName: 'amount', style: { width: '10%' } },
    { title: 'Comm.', fieldName: 'commission', style: { width: '6.5%' } },
    { title: 'VAT', fieldName: 'vat', style: { width: '6%' } },
    { title: 'Tax', fieldName: 'tax', style: { width: '6%' } },
    { title: 'Net Remit', fieldName: 'netToRemit', style: { width: '10%' } },
    { title: 'Curr.', fieldName: 'currency', style: { width: '4.5%' } }
];
export type RemittancePaymentMethodObject = {
    remittanceTransferringBank: {
        _id: BankAccount['_id'];
        name: BankAccount['accountName'];
        bankName: BankAccount['bankName'];
        currency: BankAccount['currency'];
        generalLedgerAccountNumber: BankAccount['generalLedgerAccountNumber'];
        accountNumber: BankAccount['accountNumber'];
    };
    branch: string;
    account: string;
    reference: string;
    iban: string;
    swiftCode: string;
    valueDate: Date | null;
    remittanceReceivingBank: string;
    creditCard: {
        _id: CreditCard['_id'];
        bankAccount: CreditCard['bankAccount'];
        creditCardName: CreditCard['creditCardName'];
        currency: CreditCard['currency'];
    };
    // The clearing terminal (Payments Terminals table) to charge this credit
    // card through — chosen before proceeding to the Masleka payment page.
    paymentsTerminal?: PaymentsTerminal | null;
    /** New-card (Masleka) plain instalments — ILS/USD only, per Pelecard's manual.
     *  Mutually exclusive with isCreditDeal; see CreditCardPaymentPlanFields. Never
     *  offered for a repay (RepayCardDetails), only a forward payment. */
    numberOfPayments?: number;
    /** True to run this as a CREDIT deal (עסקת קרדיט, with interest) — ILS only. */
    isCreditDeal?: boolean;
    /** The credit-instalment count, when isCreditDeal is true. */
    creditInstallments?: number;
};

export type PreRemittanceState = {
    for: string;
    supplierName: string;
    travelAgentName?: string; // optional field, only relevant to remittance issued to agent and not supplier
    amount: number;
    rate: number;
    rateAffectBase: boolean;
    currency: string | Currency;
    remark: string;
    paymentMethodType: PaymentMethodTypes;
    paymentMethodObject: RemittancePaymentMethodObject;
    // Shekel-only "Charge VAT" toggle (e.g. guide עוסק מורשה). Off for עוסק פטור.
    chargeVat?: boolean;
    /** True when this remittance gives money back rather than paying it out. */
    isRepay?: boolean;
    /**
     * One per opening of the repay dialog. The server claims it under a UNIQUE
     * index before calling Pelecard, so the same intent cannot refund twice.
     */
    repayAttemptId?: string;
    /**
     * The receipt this repay reverses, by document number.
     *
     * Persisted to `documentinfo."repayForDocumentNumber"` so the link survives
     * to the Payment Book, where the receipt is not in hand — and so a card
     * refund can resolve the card, terminal and gateway transaction from it.
     */
    repayForDocumentNumber?: number | null;
    /**
     * The same receipt by id, when it is in hand (a repay opened from the receipt
     * row). Not stored; it lets a card refund find the exact receipt rather than
     * matching by number.
     */
    repayForDocumentId?: number | string | null;
};

const DOCUMENT_TYPE = documentTypesEnum.REMITTANCE;

const Remittance: FC<Props> = (props: Props) => {
    const {
        open,
        toggleOpen,
        documentGenerationData,
        selectedDocuments,
        onAddVoucher,
        autoIssueValues,
        travelFileDocuments
    } =
        props;
    const [remittance, setRemittance] = useState<Voucher | null>(null);

    const clientsContext = useContext(ClientsContext);
    const companiesContext = useContext(CompaniesContext);
    const [isNextToMasleka, setIsNextToMasleka] = useState<boolean>(false);

    // Static lookup data is prefetched at login (usePrefetchStaticData)
    const companyInfo = useMemo(() => getPdfCompanyInfo(companiesContext), [companiesContext]);

    const authContext = useContext(AuthContext);
    const vouchersContext = useContext(VouchersContext);
    const { getConvertRate } = useContext(ExchangeRatesContext);
    const currenciesContext = useContext(CurrenciesContext);
    const localCurrency: string = (clientsContext.selected as Client)?.currencySettings
        ?.localCurrency as string;
    const primaryCurrency: string = (clientsContext.selected as Client)?.currencySettings
        ?.primaryCurrency as string;
    const localCurrencyObject: Currency | null =
        currenciesContext.dataAsArray?.find((c: Currency) => String(c._id) === String(localCurrency)) ||
        null;
    const primaryCurrencyObject: Currency | null =
        currenciesContext.dataAsArray?.find((c: Currency) => String(c._id) === String(primaryCurrency)) ||
        null;


    const fetchedTravelAgentName: string = useGetAgentInfoDetails(documentGenerationData).name;

    // Form component props:
    // Is this remittance giving money BACK, and for which receipt?
    //
    // Two ways in, and both have to end up carrying the same link:
    //   - the Accounting row action on a Receipt/Transfer Receipt, where the
    //     source document IS the receipt; and
    //   - the Payment Book, confirming a repay PRQ that already names one.
    //
    // Without it the remittance is just a payment: nothing records which receipt
    // it reverses, so nothing can find the card, the terminal or the gateway
    // transaction to refund against. The receipt is not in hand on the Payment
    // Book path, so it has to travel on the document.
    const repayLink = useMemo(() => {
        const source: any = documentGenerationData;
        const sourceInfo: any = source?.info || {};
        const isReceiptSource =
            source?.type === documentTypesEnum.RECEIPT ||
            source?.type === documentTypesEnum.TRANSFER_RECEIPT;

        // `repayForDocumentType` is deliberately NOT set. The column already
        // holds Galor's own type codes on the 34,919 imported rows (60, and 59
        // once), not ours (Receipt is 3, Transfer Receipt 6). Writing our ids
        // into the same column would put two numbering schemes in one place and
        // make any comparison across them silently wrong. The document number is
        // unique per tenant, so it identifies the receipt on its own.
        return {
            isRepay: isReceiptSource || !!sourceInfo.isRepay,
            // Also selected as a plain column on the row, for the same
            // 100-argument reason as the terminal, so check both.
            repayForDocumentNumber: isReceiptSource
                ? (source?.number ?? null)
                : (source?.repayForDocumentNumber ?? sourceInfo.repayForDocumentNumber ?? null),
            repayForDocumentId: isReceiptSource ? (source?._id ?? null) : null,
            /**
             * One key per opening of this dialog, so the same intent to refund
             * can only move money once.
             *
             * The server claims it under a UNIQUE index BEFORE calling Pelecard,
             * which covers a double click, a double submit, a browser retry and
             * an agent retrying after a timeout. Issuing a genuinely second
             * refund means reopening the dialog and getting a new key — the right
             * amount of friction for giving money back twice.
             *
             * Keyed on `open` so it is reminted each time the dialog opens rather
             * than persisting for the life of the component.
             */
            repayAttemptId:
                typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `repay-${Date.now()}-${Math.random().toString(36).slice(2)}`
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentGenerationData, open]);

    /**
     * On a repay from a receipt: the receipt's amount, what was already paid
     * back against it (linked by `repayForDocumentNumber`, a PRQ and its
     * remittance counted once), and what is left. Null for any other remittance,
     * or when the file's documents were not passed in.
     */
    const receiptRepayAllowance = useMemo(() => {
        const source: any = documentGenerationData;
        const isReceiptSource =
            source?.type === documentTypesEnum.RECEIPT || source?.type === documentTypesEnum.TRANSFER_RECEIPT;
        if (!isReceiptSource || !travelFileDocuments) return null;
        const receiptAmount = Number(source?.info?.amount) || 0;
        const currencyId = currencyIdOf(source);
        // Null when an earlier repay was in another currency: not knowable, keep the old default.
        const alreadyRepaid = repaidAgainstReceipt(travelFileDocuments, source?.number, currencyId);
        if (alreadyRepaid == null) return null;
        return {
            receiptAmount,
            alreadyRepaid,
            remaining: Math.max(0, getToFixedNumber(receiptAmount - alreadyRepaid)),
            currencyId
        };
    }, [documentGenerationData, travelFileDocuments]);

    const initialValues: PreRemittanceState = useMemo(() => {
        // For Repay from Receipt/TReceipt: extract beneficiary from the receipt
        const receiptBeneficiary = (() => {
            const info: any = documentGenerationData?.info || {};
            if (Array.isArray(info.beneficiary) && info.beneficiary.length) {
                const first = info.beneficiary[0];
                return typeof first === 'string' ? first : first?.name || '';
            }
            if (typeof info.beneficiary === 'string') return info.beneficiary;
            return info.beneficiary?.name || '';
        })();
        // When multiple vouchers are selected (covering remittance), the amount is the sum of all of them.
        const docsToSum =
            selectedDocuments && selectedDocuments.length > 1
                ? selectedDocuments
                : documentGenerationData
                  ? [documentGenerationData]
                  : [];
        const totalAmount = docsToSum.reduce(
            (acc, doc: any) => acc + Number(doc?.info?.netToRemit || doc?.info?.amount || 0),
            0
        );
        return {
            for: documentGenerationData?.info?.serviceName || '',
            supplierName: documentGenerationData?.info?.supplierName || receiptBeneficiary || '',
            travelAgentName: documentGenerationData?.info?.travelAgentName || fetchedTravelAgentName,
            // A repay starts at what is left on the receipt, not its full amount again.
            amount: receiptRepayAllowance ? receiptRepayAllowance.remaining : totalAmount,
            currency:
                (typeof documentGenerationData?.info?.currency === 'object'
                    ? (documentGenerationData?.info?.currency as any)?._id
                    : documentGenerationData?.info?.currency) || '',
            // Inherit the SOURCE doc's rate, not today's — and on a ₪ source
            // that is its ₪→$ rate (`info.rate`), because document→local is a
            // meaningless 1 there. Shared with PRQ / Receipt / Agent Invoice.
            rate:
                resolveSourceDocumentRateForForm(documentGenerationData?.info, {
                    localId: localCurrency,
                    primaryId: primaryCurrency,
                    getConvertRate
                }) ?? 1,
            rateAffectBase: true,
            remark: '',
            chargeVat: false,
            paymentMethodType: paymentMethodTypeEnum.CREDIT_CARD,
            // Carried so addNewDocument can persist them. Without these the
            // remittance is indistinguishable from an ordinary payment out.
            ...repayLink,
            paymentMethodObject: {
                valueDate: new Date(),
                overrideRateByValueDate: true,
                remittanceTransferringBank: null,
                creditCard: null,
                // Money goes back through the terminal that took it, so inherit
                // the source document's rather than starting empty and asking
                // the agent to pick — a repay on the wrong terminal cannot settle.
                //
                // Two shapes to read, because the two receipt flows store it
                // differently. An Issue-Documents receipt embeds the whole
                // terminal row under paymentMethodObject.paymentsTerminal; a
                // tour-registration receipt does not, keeping the id and number
                // in their own documentinfo columns with the name flat alongside.
                //
                // Reading only the nested one left this null on every B2C
                // receipt, and the CREDIT_CARD validation schema requires a
                // terminal — so the form silently refused to submit, with the
                // error attached to a field the repay view does not render.
                // THREE places to look, and all three are real.
                //
                // The documents query selects paymentsTerminalId/Number as plain
                // columns on the ROW rather than inside `info`: json_build_object
                // is at Postgres' 100-argument ceiling, and one more pair takes
                // the whole query down. Its own comment says consumers "read it
                // off the row or off info, whichever carried it".
                //
                // On top of that the two receipt flows differ — Issue Documents
                // embeds the terminal row under paymentMethodObject, tour
                // registration keeps only the name there.
                paymentsTerminal: (() => {
                    const source: any = documentGenerationData || {};
                    const sourceInfo: any = source.info || {};
                    const nested = sourceInfo.paymentMethodObject?.paymentsTerminal;
                    if (nested) return nested;
                    const id = source.paymentsTerminalId ?? sourceInfo.paymentsTerminalId;
                    if (!id) return null;
                    return {
                        _id: id,
                        terminalNumber:
                            source.paymentsTerminalNumber ?? sourceInfo.paymentsTerminalNumber ?? '',
                        name: sourceInfo.paymentMethodObject?.paymentsTerminalName ?? ''
                    };
                })(),
                reference: ''
            } as any // object build by paymentMethodType
        };
    }, [
        documentGenerationData,
        selectedDocuments,
        fetchedTravelAgentName,
        getConvertRate,
        localCurrency,
        primaryCurrency,
        repayLink,
        receiptRepayAllowance
    ]);

    const [paymentMethodType, setPaymentMethodType] = useState(initialValues.paymentMethodType);

    const validationSchema = useMemo(() => {
        const paymentMethodObjectValidationSchema = getPaymentMethodObjectValidationSchema(paymentMethodType);
        return yup.object().shape({
            for: yup.string(),
            amount: yup.number().required('Amount is required'),
            supplierName: yup.string(),
            travelAgentName: yup.string(),
            currency: yup.string().nullable().required('Currency is required'),
            rate: yup.number(),
            remark: yup.string(),
            paymentMethodType: yup.string(),
            paymentMethodObject: paymentMethodObjectValidationSchema
        });
    }, [paymentMethodType]);

    const [isPaidWithCreditCard, setIsPaidWithCreditCard] = useState<boolean>(true);

    /**
     * Whether this submit should open Pelecard's payment page.
     *
     * Never for a repay. "Next To Masleka" is the CHARGE page — it takes money
     * from the card. A repay gives money back, so sending one there charged the
     * customer a second time instead of refunding them, on the main path for
     * refunding a receipt (Accounting row → Repay → RMT).
     *
     * A card repay is not a payment that happens to be reversed; it is a gateway
     * refund against the original transaction, and it does not go through this
     * form's charge flow at all. Until that call exists a card repay simply
     * generates its document, exactly as cash and bank transfer already do.
     */
    const canChargeCard = isPaidWithCreditCard && !repayLink.isRepay;

    // Handle creating a new remittance document:
    const createRemittance = useCallback(
        (preRemittanceState: PreRemittanceState, actions: any) => {
            let pdfBodyBoxData: PdfBoxDataType = [],
                pdfBodyBoxData2: PdfBoxDataType = [],
                topLeftDetailsText: string = '';
            const bankAccountName =
                preRemittanceState.paymentMethodObject?.remittanceTransferringBank?.bankName || '';
            const creditCardName = preRemittanceState.paymentMethodObject?.creditCard?.creditCardName || '';

            if (preRemittanceState.paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER) {
                pdfBodyBoxData = [
                    { label: 'Bank', value: bankAccountName || '' },
                    {
                        label: 'Account',
                        value: preRemittanceState.paymentMethodObject?.remittanceTransferringBank
                            ?.accountNumber
                    }
                ];
                pdfBodyBoxData2 = [
                    { label: 'Reference', value: preRemittanceState.paymentMethodObject?.reference }
                ];
                topLeftDetailsText = bankAccountName || '';
            }
            if (preRemittanceState.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD) {
                pdfBodyBoxData = [{ label: 'Credit Card', value: creditCardName }];
                topLeftDetailsText = creditCardName || '';
                if (canChargeCard) {
                    setIsNextToMasleka(true);
                }
            }

            let documentsColumns: null | Array<PdfTablePropsColumn> = null,
                documentsRows: null | Array<PdfTablePropsRow> = null,
                documentsSummaryLine: null | {} = null;
            if (selectedDocuments) {
                documentsColumns = [...DOCUMENTS_COLUMNS];
                documentsRows = selectedDocuments?.map((voucher) => ({
                    number: `${(documentTypesInitialsEnum as any)[voucher.type]}_${voucher.number}`,
                    dueDate: `${format(new Date(voucher.info?.dueDate), 'dd/MM/yy')}`,
                    travelFileNumber: voucher.info?.travelFileNumber || '',
                    serviceName: voucher.info?.serviceName || '',
                    ref: getRef(voucher),
                    amount: taytanFormatNumberToString(voucher.info?.amount),
                    commission: taytanFormatNumberToString(voucher.info?.markup || 0),
                    vat: taytanFormatNumberToString(voucher.info?.vatOnCommission || 0),
                    netToRemit: taytanFormatNumberToString(voucher.info?.netToRemit),
                    currency: voucher.info?.currency?.code || ''
                }))!;
                // The TOTAL line is built below, after the amounts table: it is in
                // the remittance's currency and needs the rates that table resolves.
            }
            let rows, columns;
            if (preRemittanceState.paymentMethodType === paymentMethodTypeEnum.CHECK) {
                columns = [
                    { title: 'Payment Method', fieldName: 'paymentMethod', style: { width: '16%' } },
                    { title: 'Bank', fieldName: 'bank', style: { width: '22%' } },
                    { title: 'Account', fieldName: 'account', style: { width: '18%' } },
                    { title: 'Value Date', fieldName: 'dueDate', style: { width: '12%' } },
                    { title: 'Check #', fieldName: 'number', style: { width: '12%' } },
                    { title: 'Amount', fieldName: 'amount', style: { width: '10%' } },
                    { title: 'Currency', fieldName: 'currency', style: { width: '10%' } }
                ];

                if (preRemittanceState.paymentMethodType === paymentMethodTypeEnum.CHECK && !documentsRows) {
                    rows = preRemittanceState.paymentMethodObject?.checks.map((check) => ({
                        paymentMethod: preRemittanceState.paymentMethodType,
                        bank: check?.bank || '',
                        account: check?.account || '',
                        dueDate: getDateByFormat(check?.valueDate, 'dd-MMM-yy', 'No Date'),
                        number: check?.number || '',
                        amount: taytanFormatNumberToString(parseInt(preRemittanceState.amount)) || '',
                        currency: preRemittanceState.currency?.code || ''
                    }));
                }
            }

            // target is the account the money is transferred from:
            const targetAccount = (() => {
                switch (preRemittanceState.paymentMethodType) {
                    case paymentMethodTypeEnum.BANK_TRANSFER: {
                        return {
                            targetId: preRemittanceState.paymentMethodObject?.remittanceTransferringBank?._id,
                            targetType: voucherSourceTargetTypesEnum.ACCOUNT,
                            targetNumber:
                                preRemittanceState.paymentMethodObject?.remittanceTransferringBank
                                    ?.generalLedgerAccountNumber
                        };
                    }
                    case paymentMethodTypeEnum.CREDIT_CARD: {
                        return {
                            targetId: (
                                preRemittanceState.paymentMethodObject?.creditCard?.bankAccount as BankAccount
                            )?._id,
                            targetType: voucherSourceTargetTypesEnum.ACCOUNT,
                            targetNumber: (
                                preRemittanceState.paymentMethodObject?.creditCard?.bankAccount as BankAccount
                            )?.generalLedgerAccountNumber
                        };
                    }
                    case paymentMethodTypeEnum.CHECK: {
                        return null;
                    }
                    default:
                        return { targetId: undefined, targetType: undefined, targetNumber: undefined };
                }
            })();
            const isCheckPaymentMethod = preRemittanceState.paymentMethodType === paymentMethodTypeEnum.CHECK;

            // Shekel-RMT "Charge VAT": VAT is added ON TOP of the amount and persisted
            // (vatOnCommission + vatPercentage) so the Hashavshevet export can post a
            // שורת מע״מ to 250032 (מעמ תשומות). Only for local-currency (Shekel) remittances.
            // The typed amount still covers the supplier's open PRQ/voucher in full;
            // the payable total is amount + VAT.
            const rmtCurrId =
                typeof preRemittanceState.currency === 'object'
                    ? (preRemittanceState.currency as any)?._id
                    : preRemittanceState.currency;
            const rmtVatRate = getVatPercent(clientsContext.selected);
            const rmtChargeVat =
                !!preRemittanceState.chargeVat && String(rmtCurrId) === String(localCurrency);
            const rmtNet = Number(preRemittanceState.amount) || 0;
            const rmtVatAmount = rmtChargeVat ? calcVatOnTop(rmtNet, rmtVatRate) : 0;
            const rmtTotalToPay = Math.round((rmtNet + rmtVatAmount) * 100) / 100;

            // Amounts table (Amount | Tax | Local Price | Rate | Primary Amount).
            //
            // Built by the SAME helper the central builder uses, so the PDF shown
            // right after creation matches the one you get when reopening the
            // document. The two used to be hand-maintained copies and had drifted:
            // this one multiplied a shekel total by the ₪↔$ rate (a local-currency
            // doc stores the local↔primary rate, not 1 — see TableActionButtons),
            // and both derived Primary Amount from the live market rate instead of
            // the rate Change Rate had just written.
            const rmtDocCurrency =
                typeof preRemittanceState.currency === 'object'
                    ? (preRemittanceState.currency as Currency)
                    : currenciesContext.dataAsArray?.find((c: Currency) => String(c._id) === String(rmtCurrId)) ||
                      null;
            const {
                columns: amountsColumns,
                rows: amountsRows,
                summaryLine: amountsSummaryLine,
                localRate: rmtLocalRate,
                primaryToLocal: rmtPrimaryToLocal
            } = buildRemittanceAmountsTable({
                amount: rmtNet,
                vat: rmtVatAmount,
                docCurrencyId: rmtCurrId,
                docCurrencyCode: rmtDocCurrency?.code || '',
                docCurrencySymbol: rmtDocCurrency?.symbol || '',
                localCurrency: localCurrencyObject,
                primaryCurrency: primaryCurrencyObject,
                storedRate: preRemittanceState.rate,
                // On a ₪ remittance the form's rate IS ₪ per $; on a $ one it is too.
                storedPrimaryRate:
                    sameCurrency(rmtCurrId, localCurrency) || sameCurrency(rmtCurrId, primaryCurrency)
                        ? preRemittanceState.rate
                        : undefined,
                getConvertRate
            });

            // Covered-documents TOTAL, in the REMITTANCE's currency — the same
            // conversion the reopened PDF uses (convertCoveredAmountToCovering).
            // It used to total in the source document's currency here and in the
            // remittance's on reopen, unconverted: a $100 PRQ read "100.00₪".
            if (selectedDocuments) {
                const toRemittance = (n: number, docInfo: any) =>
                    convertCoveredAmountToCovering({
                        amount: n,
                        docInfo,
                        coveringCurrencyId: rmtCurrId,
                        localId: localCurrency,
                        primaryId: primaryCurrency,
                        coveringLocalRate: rmtLocalRate,
                        coveringPrimaryToLocal: rmtPrimaryToLocal,
                        getConvertRate
                    });
                const totals = (
                    selectedDocuments.length > 1 ? selectedDocuments : [documentGenerationData]
                ).reduce(
                    (acc, doc: any) => ({
                        amount: acc.amount + toRemittance(Number(doc?.info?.amount || 0), doc?.info),
                        markup: acc.markup + toRemittance(Number(doc?.info?.markup || 0), doc?.info),
                        vatOnCommission:
                            acc.vatOnCommission + toRemittance(Number(doc?.info?.vatOnCommission || 0), doc?.info),
                        netToRemit: acc.netToRemit + toRemittance(Number(doc?.info?.netToRemit || 0), doc?.info)
                    }),
                    { amount: 0, markup: 0, vatOnCommission: 0, netToRemit: 0 }
                );
                const curSym = rmtDocCurrency?.symbol || '';
                documentsSummaryLine = {
                    number: `TOTAL (${rmtDocCurrency?.code || ''})`,
                    dueDate: '',
                    travelFileNumber: '',
                    serviceName: '',
                    ref: '',
                    amount: taytanFormatNumberToString(totals.amount) + curSym,
                    commission: taytanFormatNumberToString(totals.markup) + curSym,
                    vat: taytanFormatNumberToString(totals.vatOnCommission) + curSym,
                    netToRemit: taytanFormatNumberToString(totals.netToRemit) + curSym,
                    currency: rmtDocCurrency?.code || ''
                };
            }

            const pdfInfo = {
                pdfBodyBoxData,
                pdfBodyBoxData2,
                // Main slot: cheque rows for a cheque payment, otherwise the amounts
                // table. The covered documents moved to their own slot below, which
                // RemittancePdf now renders on its own page.
                rows: isCheckPaymentMethod ? rows : amountsRows,
                columns: isCheckPaymentMethod ? columns : amountsColumns,
                summaryLine: isCheckPaymentMethod ? undefined : amountsSummaryLine,
                documentsColumns,
                documentsRows,
                documentsSummaryLine,
                topLeftDetailsText,
                bottomDetails: preRemittanceState.for,
                transferredFrom:
                    preRemittanceState.paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER
                        ? preRemittanceState.paymentMethodObject?.remittanceTransferringBank?.name
                        : creditCardName,
                // the name of the supplier/agent the payment is transferred to:
                transferredTo:
                    documentGenerationData?.info?.supplierName ||
                    documentGenerationData?.info?.travelAgentName,
                companyInfo,
                userName:
                    (authContext.currUser as any)?.username || authContext.currUser?.name || 'No User Name',
                voucherCertification: voucherCertificationTypesEnum.COPY,
                pdfImageSource: (clientsContext.selected as Client)?.companyLogoUrl
            };

            vouchersContext
                .addDocument({
                    type: DOCUMENT_TYPE,
                    sourceId: documentGenerationData?.sourceId || documentGenerationData?.targetId,
                    sourceType: documentGenerationData?.sourceType || documentGenerationData?.targetType,
                    sourceNumber:
                        documentGenerationData?.sourceNumber || documentGenerationData?.targetNumber,
                    ...targetAccount,
                    info: {
                        ...preRemittanceState,
                        // Form's `rate` is local→doc (matches RateControlBox label)
                        // — EXCEPT when the selected currency is the local one,
                        // where the box switches to local→PRIMARY (₪ per $) and
                        // localRate is 1 by definition.
                        localRate: sameCurrency(
                            typeof preRemittanceState.currency === 'object'
                                ? (preRemittanceState.currency as any)?._id
                                : preRemittanceState.currency,
                            localCurrency
                        )
                            ? 1
                            : preRemittanceState.rate,
                        paymentMethodObject: omit(preRemittanceState.paymentMethodObject, 'paymentCurrency'),
                        dueDate: preRemittanceState.paymentMethodObject.valueDate,
                        // Multi-voucher Remittance covers every selected voucher; single-select falls back to the one doc's isCovering
                        isCovering:
                            selectedDocuments && selectedDocuments.length > 1
                                ? selectedDocuments.map((d) => d._id)
                                : documentGenerationData?.info?.isCovering,
                        pdfInfo: pdfInfo,
                        // Persisted VAT for Shekel "Charge VAT" remittances (0 otherwise).
                        // A non-zero value on an RMT signals the export to emit the שורת מע״מ.
                        // `amount` stays the net (what covers the supplier's open documents);
                        // netToRemit carries the payable total so the PDF and the bank agree.
                        ...(rmtVatAmount > 0
                            ? {
                                  vatOnCommission: rmtVatAmount,
                                  vatPercentage: rmtVatRate,
                                  netToRemit: rmtTotalToPay
                              }
                            : {})
                    },
                    isPaid: false,
                    currency: preRemittanceState.currency || (documentGenerationData?.currency as Currency),
                    travelFileId: documentGenerationData?.travelFileId || '',
                    travelFileType: documentGenerationData?.travelFileType || 'client'
                })
                .then((newRemittance) => {
                    // pdfInfo is not persisted in DB — merge it back so ViewVoucherPdf can render
                    setRemittance({ ...newRemittance, info: { ...newRemittance.info, pdfInfo } });
                })
                .catch((err) => {
                    displayToast(
                        'error',
                        `Error!`,
                        `Error while creating ${DOCUMENT_TYPE}` || 'Something went wrong'
                    );
                    throw err;
                })
                .finally(() => {
                    actions.setSubmitting(false);
                });
        },
        [
            selectedDocuments,
            vouchersContext,
            documentGenerationData,
            companyInfo,
            authContext.currUser,
            clientsContext.selected,
            getConvertRate,
            localCurrency,
            primaryCurrency,
            localCurrencyObject,
            primaryCurrencyObject,
            currenciesContext.dataAsArray,
            canChargeCard
        ]
    );

    const handleSubmit = useCallback(
        async (preRemittanceState: PreRemittanceState, actions: any) => {
            if (preRemittanceState.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD) {
                if (canChargeCard) {
                    setIsNextToMasleka(true);
                } else {
                    // A card REPAY has no charge step — Masleka would take money
                    // rather than give it back. Issue the document, as the
                    // non-card methods already do. The gateway refund against the
                    // original transaction hangs off here once it exists.
                    createRemittance(preRemittanceState, actions);
                }
            } else createRemittance(preRemittanceState, actions);
        },
        [createRemittance, canChargeCard]
    );

    // Resume path: a card charge just completed and the gateway reloaded the page.
    // The saved form values arrive via autoIssueValues, so issue the document
    // straight away (the card step is already done) and let the PDF open below.
    const autoIssuedRef = useRef(false);
    useEffect(() => {
        if (!autoIssueValues || autoIssuedRef.current) return;
        // After a full page reload the contexts refetch; wait until the tenant
        // client AND the currency list are actually loaded before issuing, so
        // companyInfo / localCurrency / currency objects are populated.
        //
        // NOTE: clientsContext.selected initializes to `{}` (a truthy empty
        // object), so a plain `!clientsContext.selected` check never waits — it
        // fired createRemittance immediately with an empty client (no
        // currencySettings), which quietly produced nothing. Require a real _id
        // + currencySettings, and a non-empty currencies list.
        const selectedClient = clientsContext.selected as Client;
        if (!selectedClient?._id || !selectedClient?.currencySettings) return;
        if (!currenciesContext.dataAsArray?.length) return;
        autoIssuedRef.current = true;
        console.info('[CardRemittance] auto-issuing remittance', { autoIssueValues });
        createRemittance(autoIssueValues, { setSubmitting: () => {} });
    }, [autoIssueValues, createRemittance, clientsContext.selected, currenciesContext.dataAsArray]);

    return (
        <>
            <GeneralDialogForm
                open={open}
                title="Remittance Advice"
                onClose={toggleOpen}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                maxWidth="sm"
                minHeight="600px"
                hasCancelButton
                actionText={canChargeCard ? 'Next To Masleka' : `Generate ${DOCUMENT_TYPE}`}
            >
                <RemittanceForm
                    setPaymentMethodType={setPaymentMethodType}
                    isNextToMasleka={isNextToMasleka}
                    setIsNextToMasleka={setIsNextToMasleka}
                    documentGenerationData={documentGenerationData}
                    setIsPaidWithCreditCard={setIsPaidWithCreditCard}
                    selectedDocuments={selectedDocuments}
                    open={open}
                    toggleOpen={toggleOpen}
                    repayAllowance={receiptRepayAllowance}
                    onCardPaymentSuccess={(vals: PreRemittanceState) =>
                        createRemittance(vals, { setSubmitting: () => {} })
                    }
                />
            </GeneralDialogForm>
            {!!remittance && (
                <ViewVoucherPdf
                    voucherProps={remittance}
                    toggleVoucherModalOpen={() => {
                        setRemittance(null);
                        toggleOpen(false);
                        onAddVoucher && onAddVoucher();
                    }}
                />
            )}
        </>
    );
};

export default memo(Remittance);
